import "./products.css";

import React from "react";
import { useLocation } from "react-router-dom";

import { PRODUCTS, PRODUCT_RANGES } from "../../data/content";
import PageBanner from "../general/PageBanner";
import PageMeta from "../general/PageMeta";

const rangeFromHash = (hash) => {
  const slug = hash.replace("#", "");
  return PRODUCT_RANGES.some((range) => range.slug === slug) ? slug : PRODUCT_RANGES[0].slug;
};

const Products = () => {
  const { hash } = useLocation();
  const [active, setActive] = React.useState(() => rangeFromHash(hash));
  const tabRefs = React.useRef({});

  //Links like /products#noodles (from the home page strip) open that tab
  React.useEffect(() => {
    setActive(rangeFromHash(hash));
  }, [hash]);

  //Remember the tab in the address bar, so it can be shared / survives a refresh. Done outside the router so the page doesn't jump
  const selectRange = (slug, shouldFocus = false) => {
    setActive(slug);
    window.history.replaceState(window.history.state, "", `#${slug}`);
    if (shouldFocus) {
      tabRefs.current[slug].focus();
    }
  };

  //Arrow keys move between tabs (standard tab keyboard behaviour)
  const onTabKeyDown = (e, index) => {
    const moves = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let next = null;
    if (moves[e.key]) {
      next = (index + moves[e.key] + PRODUCT_RANGES.length) % PRODUCT_RANGES.length;
    }
    else if (e.key === "Home") {
      next = 0;
    }
    else if (e.key === "End") {
      next = PRODUCT_RANGES.length - 1;
    }
    if (next !== null) {
      e.preventDefault();
      selectRange(PRODUCT_RANGES[next].slug, true);
    }
  };

  const range = PRODUCT_RANGES.find((r) => r.slug === active);

  return (
    <React.Fragment>
      <PageMeta
        title={"Products"}
        description={`${PRODUCTS.SUBTITLE} Snax Stix, Pyramid Crackers, Air-Popped Popcorn, No Calorie Noodles, Popcorn Kernels and Candy Floss.`}
      />
      <PageBanner
        title={PRODUCTS.TITLE}
        subtitle={PRODUCTS.SUBTITLE}
        color={"var(--yellow)"}
        divider={"zigzag-up"}
        dividerColor={"var(--yellow)"}
      />
      <div
        className={"stripe-band"}
        style={{ "--band": "var(--yellow)" }}
      />

      <section className={"products"}>
        <div className={"container"}>
          <div
            className={"product-tabs"}
            role={"tablist"}
            aria-label={"Product ranges"}
          >
            {PRODUCT_RANGES.map((r, index) => (
              <button
                key={r.slug}
                ref={(el) => {
                  tabRefs.current[r.slug] = el;
                }}
                id={r.slug}
                type={"button"}
                role={"tab"}
                className={`product-tab ${r.slug === active ? "is-active" : ""}`}
                aria-selected={r.slug === active}
                aria-controls={"product-panel"}
                tabIndex={r.slug === active ? 0 : -1}
                onClick={() => selectRange(r.slug)}
                onKeyDown={(e) => onTabKeyDown(e, index)}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div
            id={"product-panel"}
            role={"tabpanel"}
            aria-labelledby={active}
            className={"product-grid"}
          >
            {range.products.map((product) => (
              <article
                key={product.slug}
                className={"product-card"}
              >
                <img
                  src={product.image}
                  alt={`${product.type} - ${product.name}`}
                  className={"product-image"}
                  width={1000}
                  height={1000}
                />
                <div className={"product-card-body"}>
                  <h3 className={"product-name"}>{product.name}</h3>
                  <p className={"product-type"}>{product.type}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <div
        className={"stripe-band"}
        style={{ "--band": "var(--yellow)" }}
      />
    </React.Fragment>
  );
};

export default Products;
